import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, BellRing, ChevronRight, Columns3, Keyboard, Rows3 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Badge, Dot } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Dialog } from '../components/ui/Dialog.js';
import { Checkbox } from '../components/ui/Field.js';
import { PageHeader, SegmentedControl } from '../components/ui/Layout.js';
import { EmptyState, ErrorBanner, SkeletonRows } from '../components/ui/States.js';
import { AssignDialog, SnoozeDialog } from '../features/alarms/AlarmDialogs.js';
import { AlarmFilterBar } from '../features/alarms/AlarmFilterBar.js';
import { AlarmCards } from '../features/alarms/AlarmCards.js';
import { AlarmTable } from '../features/alarms/AlarmTable.js';
import {
  ALARM_COLUMNS,
  PHONE_COLUMNS,
  type AlarmColumnKey,
  type Density,
} from '../features/alarms/columns.js';
import { BulkActionBar } from '../features/alarms/BulkActionBar.js';
import { DslEditor } from '../features/alarms/DslEditor.js';
import { readDefaultViewId } from '../features/alarms/defaultView.js';
import {
  defaultFilters,
  filtersFromSavedView,
  filtersFromSearchParams,
  filtersToQuery,
  filtersToSearchParams,
  type AlarmFilters,
} from '../features/alarms/filters.js';
import { groupAlarms, viewFromServerGroups } from '../features/alarms/grouping.js';
import { SavedViews } from '../features/alarms/SavedViews.js';
import { StormBanner } from '../features/policies/StormBanner.js';
import { ALARMS_KEY, useAlarmActions } from '../features/alarms/useAlarmActions.js';
import { alarmsApi, viewsApi, type SnoozeInput } from '../lib/api.js';
import { cx } from '../lib/cx.js';
import { errorMessage } from '../lib/errors.js';
import { formatCount } from '../lib/format.js';
import { useHotkeys } from '../lib/hotkeys.js';
import { useDirectory } from '../lib/directory.js';
import { PHONE_QUERY, useMediaQuery } from '../lib/useMediaQuery.js';
import { useLiveAlarms, type LiveEvent } from '../lib/live.js';
import type { Alarm, AlarmPage, BulkResponse, Severity } from '../lib/schemas.js';
import { useSession } from '../lib/session.js';
import { SEVERITY_TOKENS } from '../lib/severity.js';
import { toast } from '../lib/toast.js';

const PAGE_SIZE = 50;
const DENSITY_KEY = 'vam.alarms.density';
const COLUMNS_KEY = 'vam.alarms.columns';

function readStored<T>(key: string, fallback: T, parse: (raw: string) => T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* preference simply does not persist */
  }
}

/**
 * The alarm list: saved views, filter bar, optional grouping, virtualised
 * table, bulk actions, live inserts behind an "n new" badge and full keyboard
 * navigation.
 */
export function AlarmsPage() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const session = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<AlarmFilters>(() => filtersFromSearchParams(searchParams));
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [pendingLive, setPendingLive] = useState<string[]>([]);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [dslOpen, setDslOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [density, setDensity] = useState<Density>(() =>
    readStored<Density>(DENSITY_KEY, 'comfortable', (raw) =>
      raw === 'compact' ? 'compact' : 'comfortable',
    ),
  );
  const [visibleColumns, setVisibleColumns] = useState<Set<AlarmColumnKey>>(() =>
    readStored(
      COLUMNS_KEY,
      new Set(ALARM_COLUMNS.map((c) => c.key)),
      (raw) => new Set(JSON.parse(raw) as AlarmColumnKey[]),
    ),
  );
  const snoozeTargetRef = useRef<Alarm | null>(null);

  const defaultViewApplied = useRef(false);

  useEffect(() => {
    store(DENSITY_KEY, density);
  }, [density]);
  useEffect(() => {
    store(COLUMNS_KEY, JSON.stringify([...visibleColumns]));
  }, [visibleColumns]);

  // Keep the URL in step so a filtered list is shareable. Writing the same
  // query string again would make a new location on every render, so compare
  // first and only replace when something actually changed.
  const nextSearch = filtersToSearchParams(filters).toString();
  useEffect(() => {
    if (nextSearch === searchParams.toString()) return;
    setSearchParams(nextSearch, { replace: true });
  }, [nextSearch, searchParams, setSearchParams]);

  // Apply the remembered default view once, when nothing came from the URL.
  useEffect(() => {
    if (defaultViewApplied.current) return;
    defaultViewApplied.current = true;
    const defaultId = readDefaultViewId();
    if (!defaultId || searchParams.toString()) return;
    void viewsApi
      .list()
      .then((page) => {
        const view = page.items.find((item) => item.id === defaultId);
        if (!view) return;
        setActiveViewId(view.id);
        setFilters(filtersFromSavedView(view));
      })
      .catch(() => {
        /* saved views unavailable; the plain list is still fine */
      });
  }, [searchParams]);

  const query = useInfiniteQuery({
    queryKey: [...ALARMS_KEY, filtersToQuery(filters)],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      alarmsApi.list(filtersToQuery(filters, { limit: PAGE_SIZE, cursor: pageParam })),
    getNextPageParam: (lastPage: AlarmPage) => lastPage.next_cursor ?? undefined,
  });

  const isPhone = useMediaQuery(PHONE_QUERY);
  const directory = useDirectory();
  const groupLabels = useMemo(
    () => ({ integration: directory.integrationName, team: directory.teamName }),
    [directory],
  );

  const alarms = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.items),
    [query.data],
  );
  const serverGroups = query.data?.pages?.[0]?.groups;
  const groups = useMemo(() => {
    if (!filters.group_by) return [];
    if (serverGroups) return viewFromServerGroups(serverGroups, filters.group_by, groupLabels);
    return groupAlarms(alarms, filters.group_by, groupLabels);
  }, [serverGroups, alarms, filters.group_by, groupLabels]);

  const actions = useAlarmActions(session.data?.user?.display_name ?? session.data?.label);

  // The feed delivers notices, not alarms, so ids are buffered behind the
  // badge and the rows are fetched only when the viewer asks for them.
  const onLive = useCallback((event: LiveEvent) => {
    setPendingLive((current) =>
      current.includes(event.alarmId) ? current : [event.alarmId, ...current].slice(0, 200),
    );
  }, []);
  useLiveAlarms(onLive);

  const flushLive = (): void => {
    if (pendingLive.length === 0) return;
    const ids = pendingLive;
    setPendingLive([]);
    void Promise.all(ids.map((alarmId) => alarmsApi.get(alarmId).catch(() => null))).then(
      (results) => {
        const fetched = results.filter((alarm): alarm is Alarm => alarm !== null);
        if (fetched.length === 0) return;
        client.setQueryData(
          [...ALARMS_KEY, filtersToQuery(filters)],
          (data: { pages: AlarmPage[]; pageParams: unknown[] } | undefined) => {
            if (!data) return data;
            const [first, ...rest] = data.pages;
            if (!first) return data;
            const existing = new Set(first.items.map((alarm) => alarm.id));
            const inserted = fetched.filter((alarm) => !existing.has(alarm.id));
            return {
              ...data,
              pages: [
                {
                  ...first,
                  items: [
                    ...inserted,
                    ...first.items.map(
                      (alarm) => fetched.find((live) => live.id === alarm.id) ?? alarm,
                    ),
                  ],
                },
                ...rest,
              ],
            };
          },
        );
        setHighlighted(new Set(fetched.map((alarm) => alarm.id)));
        setTimeout(() => setHighlighted(new Set()), 2000);
      },
    );
  };

  const bulk = useMutation({
    mutationFn: ({ action, payload }: { action: string; payload?: Record<string, unknown> }) =>
      action === 'snooze'
        ? // The API has no bulk snooze; one request per alarm, reported the
          // same way the server reports a bulk action.
          snoozeEach([...selected], payload as unknown as SnoozeInput)
        : alarmsApi.bulk(action, [...selected], payload ?? {}),
    onSuccess: async (outcome, variables) => {
      // Each id succeeds or fails on its own: an already-closed alarm in an
      // acknowledge batch is a 409 for that one row, not a green toast for all.
      const failed = outcome.results.filter((result) => !result.ok);
      if (failed.length === 0) {
        toast.success(`${outcome.succeeded} alarm${outcome.succeeded === 1 ? '' : 's'} updated`, {
          description: `Action: ${variables.action}`,
        });
        setSelected(new Set());
      } else {
        toast.error(`${failed.length} of ${outcome.results.length} failed`, {
          description: failed[0]?.detail ?? `Action: ${variables.action}`,
        });
        setSelected(new Set(failed.map((result) => result.id)));
      }
      await client.invalidateQueries({ queryKey: ALARMS_KEY });
    },
    onError: (error) => toast.error('Bulk action failed', { description: errorMessage(error) }),
  });

  const toggle = (id: string): void =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (): void =>
    setSelected((current) =>
      current.size === alarms.length ? new Set() : new Set(alarms.map((alarm) => alarm.id)),
    );

  const activeAlarm = alarms[activeIndex];

  useHotkeys(
    {
      j: () => setActiveIndex((i) => Math.min(alarms.length - 1, i + 1)),
      k: () => setActiveIndex((i) => Math.max(0, i - 1)),
      a: () => activeAlarm && actions.ack(activeAlarm),
      c: () => activeAlarm && actions.close(activeAlarm),
      s: () => {
        if (!activeAlarm) return;
        snoozeTargetRef.current = activeAlarm;
        setSnoozeOpen(true);
      },
      o: () => {
        if (activeAlarm) void navigate(`/alarms/${activeAlarm.id}`);
      },
      x: () => activeAlarm && toggle(activeAlarm.id),
      '?': () => setHelpOpen(true),
    },
    // A dialog focuses a button, which the hotkey filter does not treat as an
    // input, so 'c' inside the snooze dialog would close the alarm behind it.
    !(snoozeOpen || assignOpen || dslOpen || helpOpen || columnsOpen),
  );

  const onSnoozeConfirm = (input: SnoozeInput): void => {
    const target = snoozeTargetRef.current;
    if (target) {
      actions.snooze(target, input);
      snoozeTargetRef.current = null;
    } else if (selected.size > 0) {
      bulk.mutate({ action: 'snooze', payload: input as Record<string, unknown> });
    }
    setSnoozeOpen(false);
  };

  // Eight columns in 390 px is unreadable; a phone gets severity, title and age.
  const shownColumns = useMemo(
    () =>
      isPhone ? new Set(PHONE_COLUMNS.filter((key) => visibleColumns.has(key))) : visibleColumns,
    [isPhone, visibleColumns],
  );

  const tableProps = {
    directory,
    selected,
    onToggle: toggle,
    onToggleAll: toggleAll,
    visibleColumns: shownColumns,
    density,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
    highlighted,
    onAck: actions.ack,
    onClose: (alarm: Alarm) => actions.close(alarm),
    onUnack: actions.unack,
    onSnooze: (alarm: Alarm) => {
      snoozeTargetRef.current = alarm;
      setSnoozeOpen(true);
    },
  };

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Alarms"
        description="Everything the pipeline has correlated, newest activity first."
        actions={
          <>
            {pendingLive.length > 0 && (
              <Button variant="primary" size="md" onClick={flushLive}>
                <ArrowDown className="h-3.5 w-3.5" />
                {pendingLive.length} new
              </Button>
            )}
            {/* Density, the column chooser and the shortcut sheet are desktop
                affordances; a phone has neither the width nor a keyboard. */}
            <span className="hidden items-center gap-2 sm:flex">
              <SegmentedControl
                label="Row density"
                value={density}
                onChange={setDensity}
                options={[
                  {
                    value: 'comfortable',
                    label: <Rows3 className="h-3.5 w-3.5" />,
                    title: 'Comfortable rows',
                  },
                  {
                    value: 'compact',
                    label: <Columns3 className="h-3.5 w-3.5" />,
                    title: 'Compact rows',
                  },
                ]}
              />
              <Button variant="secondary" onClick={() => setColumnsOpen(true)}>
                <Columns3 className="h-3.5 w-3.5" />
                Columns
              </Button>
              <Button
                variant="ghost"
                onClick={() => setHelpOpen(true)}
                aria-label="Keyboard shortcuts"
              >
                <Keyboard className="h-3.5 w-3.5" />
              </Button>
            </span>
          </>
        }
      />

      {/* Few notifications during a flood is the guard working, not the
          pipeline failing; the alarm list is where an operator notices. */}
      <StormBanner />

      <details className="group rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] lg:hidden">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-[var(--text-base)] font-medium text-[var(--text)]">
          <ChevronRight
            className="h-3.5 w-3.5 transition-transform duration-[var(--dur-fast)] group-open:rotate-90"
            aria-hidden
          />
          Saved views
        </summary>
        <div className="border-t border-[var(--border)] p-2">
          <SavedViews
            currentUserId={session.data?.user?.id ?? null}
            currentTeamIds={session.data?.team_ids ?? []}
            filters={filters}
            activeViewId={activeViewId}
            onApply={(view, next) => {
              setActiveViewId(view.id);
              setFilters(next);
            }}
            onClearView={() => {
              setActiveViewId(null);
              setFilters(defaultFilters());
            }}
            className="border-0 bg-transparent p-0"
          />
        </div>
      </details>

      <div
        className={cx(
          'flex flex-col gap-3 lg:flex-row',
          // The bulk bar is sticky at the foot of the list: without room under
          // it, it covers the last rows (and, on a phone, is covered by the
          // bottom bar in turn).
          selected.size > 0 && 'pb-24 sm:pb-16',
        )}
      >
        <SavedViews
          className="hidden self-start lg:flex lg:w-[220px] lg:shrink-0"
          currentUserId={session.data?.user?.id ?? null}
          currentTeamIds={session.data?.team_ids ?? []}
          filters={filters}
          activeViewId={activeViewId}
          onApply={(view, next) => {
            setActiveViewId(view.id);
            setFilters(next);
          }}
          onClearView={() => {
            setActiveViewId(null);
            setFilters(defaultFilters());
          }}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <AlarmFilterBar
            filters={filters}
            onChange={(next) => {
              setActiveViewId(null);
              setFilters(next);
            }}
            onOpenAdvanced={() => setDslOpen(true)}
          />

          {query.isPending ? (
            <SkeletonRows
              rows={10}
              className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]"
            />
          ) : query.error ? (
            <ErrorBanner
              title="Could not load alarms"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : (filters.group_by ? groups.length === 0 : alarms.length === 0) ? (
            <EmptyState
              icon={<BellRing className="h-6 w-6" />}
              title="No alarms match these filters"
              description="Either everything is healthy, or the filters are too narrow. Clear them to see the full list."
              action={
                <Button variant="primary" onClick={() => setFilters(defaultFilters())}>
                  Clear filters
                </Button>
              }
            />
          ) : filters.group_by ? (
            <div className="flex flex-col gap-2">
              {groups.map((group, groupIndex) => {
                const isCollapsed = collapsed.has(group.key);
                const groupIds = group.items.map((alarm) => alarm.id);
                const groupSelected = groupIds.filter((id) => selected.has(id));
                const severityInk =
                  filters.group_by === 'severity' && group.key in SEVERITY_TOKENS
                    ? SEVERITY_TOKENS[group.key as Severity].ink
                    : null;
                return (
                  <section key={group.key} className="group/section flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        aria-expanded={!isCollapsed}
                        onClick={() =>
                          setCollapsed((current) => {
                            const next = new Set(current);
                            if (next.has(group.key)) next.delete(group.key);
                            else next.add(group.key);
                            return next;
                          })
                        }
                        className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-1 py-0.5 text-[var(--text-base)] font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
                      >
                        <ChevronRight
                          className={cx(
                            'h-3.5 w-3.5 transition-transform duration-[var(--dur-fast)]',
                            !isCollapsed && 'rotate-90',
                          )}
                          aria-hidden
                        />
                        {severityInk && <Dot color={severityInk} />}
                        {group.label}
                        <Badge>{formatCount(group.count)}</Badge>
                      </button>
                      {group.count > group.items.length && (
                        <span className="text-[var(--text-sm)] text-[var(--text-3)]">
                          showing {group.items.length} of {formatCount(group.count)}
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="opacity-0 transition-opacity duration-[var(--dur-fast)] focus-visible:opacity-100 group-hover/section:opacity-100"
                        onClick={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            const allIn = groupIds.every((id) => next.has(id));
                            for (const id of groupIds) {
                              if (allIn) next.delete(id);
                              else next.add(id);
                            }
                            return next;
                          })
                        }
                      >
                        {groupSelected.length === groupIds.length
                          ? 'Deselect group'
                          : 'Select group'}
                      </Button>
                      {groupSelected.length > 0 && (
                        <BulkActionBar
                          inline
                          className="ml-auto"
                          count={groupSelected.length}
                          pending={bulk.isPending}
                          onAck={() => bulk.mutate({ action: 'ack' })}
                          onClose={() => bulk.mutate({ action: 'close' })}
                          onSnooze={() => setSnoozeOpen(true)}
                          onAssign={() => setAssignOpen(true)}
                          onClear={() => setSelected(new Set())}
                        />
                      )}
                    </div>
                    {!isCollapsed &&
                      (isPhone ? (
                        <AlarmCards
                          alarms={group.items}
                          directory={directory}
                          selected={selected}
                          onToggle={toggle}
                          highlighted={highlighted}
                        />
                      ) : (
                        <AlarmTable
                          alarms={group.items}
                          showHeader={groupIndex === 0}
                          {...tableProps}
                        />
                      ))}
                  </section>
                );
              })}
            </div>
          ) : (
            <>
              {isPhone ? (
                <AlarmCards
                  alarms={alarms}
                  directory={directory}
                  selected={selected}
                  onToggle={toggle}
                  highlighted={highlighted}
                />
              ) : (
                <AlarmTable alarms={alarms} {...tableProps} />
              )}
            </>
          )}

          {query.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          )}

          {!filters.group_by && (
            <BulkActionBar
              count={selected.size}
              pending={bulk.isPending}
              onAck={() => bulk.mutate({ action: 'ack' })}
              onClose={() => bulk.mutate({ action: 'close' })}
              onSnooze={() => setSnoozeOpen(true)}
              onAssign={() => setAssignOpen(true)}
              onClear={() => setSelected(new Set())}
            />
          )}
        </div>
      </div>

      <DslEditor
        open={dslOpen}
        onClose={() => setDslOpen(false)}
        value={filters.dsl}
        onApply={(condition) => setFilters({ ...filters, dsl: condition })}
      />

      <SnoozeDialog
        open={snoozeOpen}
        onClose={() => {
          snoozeTargetRef.current = null;
          setSnoozeOpen(false);
        }}
        onConfirm={onSnoozeConfirm}
        count={snoozeTargetRef.current ? 1 : selected.size}
        pending={bulk.isPending}
      />

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        count={selected.size}
        pending={bulk.isPending}
        onConfirm={(userId) => {
          bulk.mutate({ action: 'assign', payload: { assignee_id: userId } });
          setAssignOpen(false);
        }}
      />

      <Dialog
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        title="Columns"
        description="Choose what the table shows. The choice is remembered on this device."
        size="sm"
      >
        <div className="flex flex-col gap-2">
          {ALARM_COLUMNS.map((column) => (
            <Checkbox
              key={column.key}
              label={column.label}
              checked={visibleColumns.has(column.key)}
              onChange={() =>
                setVisibleColumns((current) => {
                  const next = new Set(current);
                  if (next.has(column.key)) next.delete(column.key);
                  else next.add(column.key);
                  return next;
                })
              }
            />
          ))}
        </div>
      </Dialog>

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Keyboard shortcuts"
        size="sm"
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[var(--text-base)]">
          {[
            ['j / k', 'Move down / up'],
            ['o', 'Open the highlighted alarm'],
            ['a', 'Acknowledge'],
            ['c', 'Close'],
            ['s', 'Snooze'],
            ['x', 'Select the highlighted row'],
            ['⌘K / Ctrl+K', 'Command palette'],
            ['?', 'This help'],
          ].map(([keys, description]) => (
            <div key={keys} className="contents">
              <dt>
                <kbd className="rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[var(--text-sm)]">
                  {keys}
                </kbd>
              </dt>
              <dd className="text-[var(--text-2)]">{description}</dd>
            </div>
          ))}
        </dl>
      </Dialog>
    </div>
  );
}

/** Bulk snooze, client side: the API snoozes one alarm per request. */
async function snoozeEach(ids: string[], input: SnoozeInput): Promise<BulkResponse> {
  const settled = await Promise.allSettled(ids.map((id) => alarmsApi.snooze(id, input)));
  const results = settled.map((outcome, index) => {
    const id = ids[index]!;
    return outcome.status === 'fulfilled'
      ? { id, ok: true, state: 'snoozed' }
      : { id, ok: false, detail: errorMessage(outcome.reason) };
  });
  return {
    action: 'snooze',
    succeeded: results.filter((result) => result.ok).length,
    failed: results.filter((result) => !result.ok).length,
    results,
  };
}
